import { useState } from "react";

export default function PartnershipInquiryForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="partner-form" className="py-24 px-6 bg-white">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left - Info */}
        <div>
          <span className="inline-flex items-center gap-2 text-[#C9A961] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-[#C9A961]" />
            Become a Partner
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
            Start a Partnership Conversation Today
          </h2>
          <p className="text-[#6B6B6B] text-base leading-relaxed mb-8">
            Whether you represent an institution, a community organization, or a mission-aligned venture, we invite you to reach out. Complete the form and our team will be in touch within 2&ndash;3 business days to schedule an initial conversation.
          </p>

          <div className="space-y-5 mb-10">
            {[
              {
                icon: "ri-time-line",
                title: "No Commitment Required",
                desc: "The initial conversation is simply about understanding your organization and exploring whether there is a meaningful opportunity to collaborate.",
              },
              {
                icon: "ri-shake-hands-line",
                title: "Relationship-First Approach",
                desc: "We take the time to understand your goals, your community, and your mission before proposing any collaboration pathway.",
              },
              {
                icon: "ri-lock-line",
                title: "Confidential &amp; Professional",
                desc: "All partnership conversations are handled with the highest level of discretion, professionalism, and mutual respect.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#1A4D3C] text-base`} />
                </div>
                <div>
                  <div
                    className="font-semibold text-[#2B2D2F] text-sm mb-0.5"
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                  <div
                    className="text-[#6B6B6B] text-xs leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                  />
                </div>
              </div>
            ))}
          </div>

          <img
            src="/assets/images/site/part-form-001-600x380.jpg"
            alt="Harambee Global Ventures partnership inquiry collaboration"
            className="w-full h-[260px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Right - Form */}
        <div className="bg-[#F8F6F3] rounded-lg p-8 md:p-10 border border-[#E8E4DC]">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-[#1A4D3C] text-3xl" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#2B2D2F] mb-3">
                Inquiry Received
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-sm mx-auto">
                Thank you for reaching out to Harambee Global Ventures. Our partnerships team will review your submission and be in touch within 2&ndash;3 business days to schedule an initial conversation.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-1">
                Partnership Inquiry
              </h3>
              <p className="text-[#6B6B6B] text-sm mb-7">
                All fields marked with * are required.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="Your first name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Your last name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@organization.com"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    name="organization_name"
                    required
                    placeholder="Your organization or institution"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Your Role / Title
                  </label>
                  <input
                    type="text"
                    name="role"
                    placeholder="e.g. Executive Director, CEO, Partnership Manager"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Country / Region *
                  </label>
                  <input
                    type="text"
                    name="country"
                    required
                    placeholder="e.g. Kenya, United States, United Kingdom"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Organization Type *
                  </label>
                  <select
                    name="organization_type"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select your organization type</option>
                    <option value="Development Finance Institution">Development Finance Institution</option>
                    <option value="Public-Private Partnership Stakeholder">Public-Private Partnership Stakeholder</option>
                    <option value="Family Office">Family Office</option>
                    <option value="Diaspora Network or Association">Diaspora Network or Association</option>
                    <option value="Educational Organization">Educational or Workforce Development Organization</option>
                    <option value="Community Organization">Community Organization</option>
                    <option value="Strategic Advisory Firm">Strategic Business or Advisory Firm</option>
                    <option value="Mission-Aligned Venture">Mission-Aligned Venture</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Partnership Interest *
                  </label>
                  <select
                    name="partnership_interest"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select primary area of interest</option>
                    <option value="Co-Hosted Educational Programs">Co-Hosted Educational Programs</option>
                    <option value="Webinars and Workshops">Webinars &amp; Workshops</option>
                    <option value="Strategic Outreach Initiatives">Strategic Outreach Initiatives</option>
                    <option value="Investor-Readiness Programming">Investor-Readiness Programming</option>
                    <option value="Cross-Border Advisory Collaboration">Cross-Border Advisory Collaboration</option>
                    <option value="Workforce Development">Workforce Development Initiatives</option>
                    <option value="Community Engagement">Community Engagement Partnerships</option>
                    <option value="Ecosystem Building">Long-Term Ecosystem Building</option>
                    <option value="General Inquiry">General Inquiry / Not Sure Yet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    Tell Us About Your Organization &amp; Goals
                    <span className="ml-2 text-[#ABABAB] font-normal normal-case tracking-normal">
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    name="goals"
                    rows={4}
                    maxLength={500}
                    placeholder="Briefly describe your organization, your mission, and what you hope to achieve through a partnership with Harambee Global Ventures."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2">
                    How Did You Hear About Us?
                  </label>
                  <select
                    name="referral_source"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Word of Mouth">Word of Mouth / Referral</option>
                    <option value="Search Engine">Search Engine</option>
                    <option value="Community Event">Community Event</option>
                    <option value="Partner Organization">Partner Organization</option>
                    <option value="Trading Academy">Trading Academy</option>
                    <option value="Advisory Services">Advisory Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-md bg-[#1A4D3C] text-white font-semibold text-sm hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Partnership Inquiry"}
                </button>

                <p className="text-[#ABABAB] text-xs text-center leading-relaxed">
                  By submitting this form, you agree to be contacted by the Harambee Global Ventures partnerships team. We respect your privacy and will never share your information with third parties.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
